export type WebAccessInput = {
  /** `Platform.OS` — anything other than "web" is never gated here. */
  platform: string;
  /** Whether the auth backend is available in this build (`useAuth().configured`). */
  authConfigured: boolean;
  /** Whether the initial session lookup has finished (`useAuth().ready`). */
  authReady: boolean;
  userId: string | null;
  /** Server-truth subscription access from the signed-in user's profile. */
  hasSyncAccess: boolean;
  /** The current route is one that must work without an active subscription. */
  publicPath?: boolean;
};

/**
 * Routes that stay reachable without an account or subscription. A password
 * reset link signs the person in with a recovery session (and they may have no
 * subscription), so the paywall must not stand between them and the form.
 */
export const WEB_PUBLIC_PATHS = ["/reset-password"];

export function isWebPublicPath(pathname: string) {
  return WEB_PUBLIC_PATHS.includes(pathname.replace(/\/+$/, "") || "/");
}

/**
 * - `open`      render the app
 * - `loading`   session lookup in flight — render nothing app-like yet
 * - `sign-in`   no account yet — show the sign-in / create-account screen
 * - `subscribe` signed in but no active subscription or trial — show the paywall
 */
export type WebAccessState = "open" | "loading" | "sign-in" | "subscribe";

/**
 * Durance on the web has no local-only mode: it needs an account, and the
 * account needs an active subscription (a free trial counts — RevenueCat
 * reports it as an active entitlement and the webhook mirrors that into
 * `profiles`). Native apps are unaffected: local use there stays free and only
 * cross-device sync is gated (see `shouldUseLocalCounters`).
 *
 * A build with no auth backend at all (`authConfigured` false — an
 * unconfigured local checkout) stays open rather than dead-ending on a sign-in
 * screen that can't work; every deployed environment has the backend.
 */
export function webAccessState({
  platform,
  authConfigured,
  authReady,
  userId,
  hasSyncAccess,
  publicPath = false,
}: WebAccessInput): WebAccessState {
  if (platform !== "web") return "open";
  if (publicPath) return "open";
  if (!authConfigured) return "open";
  if (!authReady) return "loading";
  if (!userId) return "sign-in";
  if (!hasSyncAccess) return "subscribe";
  return "open";
}
